import React, { useEffect, useMemo, useState, createElement } from 'react';
import { Search, Filter, FileCode2, MoreVertical, X, ChevronDown, Eye, Copy, Play, Pause, Activity, CheckCircle2, AlertTriangle, Clock, Zap, BarChart3, Settings, GitBranch, FlaskConical, ArrowLeftRight, RotateCcw, Ban, Layers, Code, Variable, FileText, Users, TrendingUp, TrendingDown, Hash, Cpu, Timer, ShieldCheck, ShieldAlert, Sparkles, PenLine } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { Pagination } from '../ui/Pagination';
interface AITemplateEngineProps {
  success: (msg: string) => void;
  setDetailPanel: (panel: any) => void;
}
// --- TYPES ---
interface TemplateVersion {
  version: string;
  createdAt: string;
  createdBy: string;
  changes: string;
  status: 'active' | 'archived' | 'testing';
  successRate: number;
  usageCount: number;
}
interface Template {
  id: string;
  name: string;
  type: string;
  description: string;
  status: 'active' | 'draft' | 'testing' | 'disabled';
  activeVersion: string;
  versions: TemplateVersion[];
  defaultModel: string;
  fallbackModel: string;
  maxTokens: number;
  temperature: number;
  systemPrompt: string;
  userPromptTemplate: string;
  structure: string[];
  variables: {
    name: string;
    type: string;
    required: boolean;
    description: string;
  }[];
  usageCount: number;
  successRate: number;
  errorRate: number;
  avgGenTime: number;
  lastRun: string;
  abTest: {
    enabled: boolean;
    variantA: string;
    variantB: string;
    split: number;
  } | null;
  linkedServices: string[];
  createdAt: string;
  updatedAt: string;
}
// --- MOCK DATA ---
function randomFrom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
const TEMPLATE_TYPES = ['Натальная карта', 'Соляр', 'Совместимость', 'Прогноз', 'Комбо', 'Кастом'];
const MODELS = ['GPT-5', 'Claude 3.5', 'Llama 3'];
const STATUSES: Template['status'][] = ['active', 'draft', 'testing', 'disabled'];
const SYSTEM_PROMPTS: Record<string, string> = {
  'Натальная карта': 'You are an expert astrologer specializing in natal chart analysis. Based on the provided planetary positions, houses, and aspects, generate a comprehensive natal chart interpretation. Focus on personality traits, life themes, karmic lessons, and practical advice.',
  Соляр: 'Generate a detailed solar return report focusing on the main themes, opportunities, and challenges of the upcoming year. Analyze the solar return chart in relation to the natal chart.',
  Совместимость: 'Analyze the synastry chart between two individuals. Evaluate emotional compatibility, communication patterns, potential conflicts, and long-term relationship prospects.',
  Прогноз: 'Provide a detailed transit forecast for the specified period. Identify key dates, planetary influences, and practical recommendations for navigating upcoming energies.',
  Комбо: 'Generate a comprehensive combined analysis including natal chart interpretation, current transits, and personalized recommendations. Integrate multiple astrological techniques.',
  Кастом: 'You are an expert astrologer. Generate a customized astrological report based on the provided parameters and user preferences.'
};
const VARIABLE_SETS: Record<string, Template['variables']> = {
  'Натальная карта': [{
    name: 'birth_date',
    type: 'date',
    required: true,
    description: 'Дата рождения'
  }, {
    name: 'birth_time',
    type: 'time',
    required: true,
    description: 'Время рождения'
  }, {
    name: 'birth_place',
    type: 'string',
    required: true,
    description: 'Место рождения'
  }, {
    name: 'planet_positions',
    type: 'json',
    required: true,
    description: 'Позиции планет'
  }, {
    name: 'house_system',
    type: 'enum',
    required: false,
    description: 'Система домов (Placidus/Koch)'
  }],
  Совместимость: [{
    name: 'person_a_birth_date',
    type: 'date',
    required: true,
    description: 'Дата рождения A'
  }, {
    name: 'person_a_birth_time',
    type: 'time',
    required: true,
    description: 'Время рождения A'
  }, {
    name: 'person_a_birth_place',
    type: 'string',
    required: true,
    description: 'Место рождения A'
  }, {
    name: 'person_b_birth_date',
    type: 'date',
    required: true,
    description: 'Дата рождения B'
  }, {
    name: 'person_b_birth_time',
    type: 'time',
    required: false,
    description: 'Время рождения B'
  }, {
    name: 'person_b_birth_place',
    type: 'string',
    required: true,
    description: 'Место рождения B'
  }]
};
const STRUCTURES: Record<string, string[]> = {
  'Натальная карта': ['Введение', 'Солнечный знак', 'Лунный знак', 'Асцендент', 'Планеты в домах', 'Аспекты', 'Кармические задачи', 'Рекомендации'],
  Соляр: ['Главная тема года', 'Карьера и финансы', 'Отношения', 'Здоровье', 'Ключевые даты', 'Итоги'],
  Совместимость: ['Общая совместимость', 'Эмоциональная связь', 'Интеллектуальная совместимость', 'Физическое влечение', 'Конфликтные зоны', 'Перспективы'],
  Прогноз: ['Общий фон периода', 'Ключевые транзиты', 'Благоприятные дни', 'Предостережения', 'Рекомендации'],
  Комбо: ['Натальный анализ', 'Текущие транзиты', 'Прогноз на месяц', 'Персональные рекомендации'],
  Кастом: ['Секция 1', 'Секция 2', 'Секция 3']
};
function generateTemplates(count: number): Template[] {
  return Array.from({
    length: count
  }, (_, i) => {
    const type = i < 6 ? TEMPLATE_TYPES[i] : randomFrom(TEMPLATE_TYPES);
    const status = i < 5 ? 'active' : randomFrom(STATUSES);
    const versionCount = Math.floor(Math.random() * 8) + 1;
    const activeVersion = `v${Math.floor(Math.random() * 4) + 1}.${Math.floor(Math.random() * 10)}`;
    const usageCount = status === 'active' ? Math.floor(Math.random() * 5000) + 200 : Math.floor(Math.random() * 100);
    const successRate = status === 'active' ? 85 + Math.random() * 14 : 60 + Math.random() * 30;
    const errorRate = 100 - successRate;
    const hasAbTest = status === 'active' && Math.random() > 0.7;
    const versions: TemplateVersion[] = Array.from({
      length: versionCount
    }, (__, vi) => ({
      version: `v${versionCount - vi}.${Math.floor(Math.random() * 10)}`,
      createdAt: new Date(Date.now() - (vi + 1) * 7 * 24 * 60 * 60 * 1000).toISOString(),
      createdBy: randomFrom(['Админ Алексей', 'Админ Мария', 'Система', 'AI Optimizer']),
      changes: randomFrom(['Улучшен system prompt для точности', 'Добавлена секция кармических задач', 'Оптимизация токенов (-15%)', 'Исправлен формат вывода', 'Добавлены новые переменные', 'A/B тест: вариант B', 'Rollback к стабильной версии', 'Обновлена структура ответа']),
      status: vi === 0 ? 'active' : vi === 1 && hasAbTest ? 'testing' : 'archived',
      successRate: 80 + Math.random() * 19,
      usageCount: Math.floor(Math.random() * 2000)
    }));
    const defaultVars = VARIABLE_SETS[type] || [{
      name: 'birth_date',
      type: 'date',
      required: true,
      description: 'Дата рождения'
    }, {
      name: 'birth_time',
      type: 'time',
      required: false,
      description: 'Время рождения'
    }, {
      name: 'birth_place',
      type: 'string',
      required: true,
      description: 'Место рождения'
    }];
    return {
      id: `TPL-${String(i + 1).padStart(2, '0')}`,
      name: i < 6 ? type : `${type} (${randomFrom(['Premium', 'Lite', 'Extended', 'Custom', 'Beta', 'Pro'])})`,
      type,
      description: `Шаблон для генерации отчёта типа "${type}". Версия ${activeVersion}.`,
      status,
      activeVersion,
      versions,
      defaultModel: randomFrom(MODELS),
      fallbackModel: randomFrom(MODELS),
      maxTokens: randomFrom([4096, 8192, 16384]),
      temperature: randomFrom([0.5, 0.7, 0.8, 0.9]),
      systemPrompt: SYSTEM_PROMPTS[type] || SYSTEM_PROMPTS['Кастом'],
      userPromptTemplate: `Сгенерируй отчёт "${type}" для клиента.\n\nДанные:\n{{birth_date}}, {{birth_time}}, {{birth_place}}\n\nФормат: JSON с секциями.`,
      structure: STRUCTURES[type] || STRUCTURES['Кастом'],
      variables: defaultVars,
      usageCount,
      successRate: parseFloat(successRate.toFixed(1)),
      errorRate: parseFloat(errorRate.toFixed(1)),
      avgGenTime: Math.floor(Math.random() * 60) + 15,
      lastRun: new Date(Date.now() - Math.random() * 3 * 24 * 60 * 60 * 1000).toISOString(),
      abTest: hasAbTest ? {
        enabled: true,
        variantA: activeVersion,
        variantB: versions[1]?.version || 'v1.0',
        split: 50
      } : null,
      linkedServices: randomFrom([['Натальная карта (базовая)', 'Натальная карта (премиум)'], ['Соляр годовой'], ['Совместимость пар', 'Совместимость бизнес'], ['Прогноз месячный', 'Прогноз годовой']]),
      createdAt: new Date(Date.now() - Math.random() * 180 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString()
    };
  });
}
const INITIAL_DATA = generateTemplates(48);
const STATUS_CONFIG: Record<string, {
  label: string;
  variant: any;
}> = {
  active: {
    label: 'Активен',
    variant: 'success'
  },
  draft: {
    label: 'Черновик',
    variant: 'neutral'
  },
  testing: {
    label: 'Тестирование',
    variant: 'warning'
  },
  disabled: {
    label: 'Отключён',
    variant: 'error'
  }
};
export function AITemplateEngine({
  success,
  setDetailPanel
}: AITemplateEngineProps) {
  const [data, setData] = useState(INITIAL_DATA);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    model: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [detailTab, setDetailTab] = useState('main');
  const [sortField, setSortField] = useState('usageCount');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // New states for editing, creating, and testing
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [templateForm, setTemplateForm] = useState<Partial<Template>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  useEffect(() => {
    const handleClick = () => setOpenMenuId(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
  // Stats
  const stats = useMemo(() => {
    const active = data.filter(d => d.status === 'active');
    const testing = data.filter(d => d.status === 'testing');
    const avgSuccess = active.length > 0 ? active.reduce((s, d) => s + d.successRate, 0) / active.length : 0;
    const avgTime = active.length > 0 ? active.reduce((s, d) => s + d.avgGenTime, 0) / active.length : 0;
    const problematic = data.filter(d => d.errorRate > 15 && d.status === 'active');
    return {
      total: data.length,
      active: active.length,
      testing: testing.length,
      avgSuccess: avgSuccess.toFixed(1),
      avgTime: avgTime.toFixed(0),
      problematic: problematic.length
    };
  }, [data]);
  // Filter & Sort
  const filteredData = useMemo(() => {
    let result = data.filter(item => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q || item.id.toLowerCase().includes(q) || item.name.toLowerCase().includes(q) || item.type.toLowerCase().includes(q);
      const matchesStatus = filters.status === 'all' || item.status === filters.status;
      const matchesType = filters.type === 'all' || item.type === filters.type;
      const matchesModel = filters.model === 'all' || item.defaultModel === filters.model;
      return matchesSearch && matchesStatus && matchesType && matchesModel;
    });
    result.sort((a, b) => {
      let cmp = 0;
      const key = sortField as keyof Template;
      const av = a[key];
      const bv = b[key];
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;else if (typeof av === 'string' && typeof bv === 'string') cmp = av.localeCompare(bv);
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return result;
  }, [data, searchQuery, filters, sortField, sortDir]);
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');else {
      setSortField(field);
      setSortDir('desc');
    }
  };
  const openDetail = (t: Template, edit = false) => {
    setSelectedTemplate(t);
    setTemplateForm({
      ...t
    });
    setEditMode(edit);
    setDetailTab('main');
    setShowDetail(true);
    setTestResult(null);
  };
  const handleCreate = () => {
    if (!templateForm.name || !templateForm.type) {
      success('Заполните название и тип шаблона');
      return;
    }
    const newId = `TPL-${String(data.length + 1).padStart(2, '0')}`;
    const newTemplate: Template = {
      id: newId,
      name: templateForm.name,
      type: templateForm.type,
      description: templateForm.description || '',
      status: 'draft',
      activeVersion: 'v1.0',
      versions: [{
        version: 'v1.0',
        createdAt: new Date().toISOString(),
        createdBy: 'Вы',
        changes: 'Создание шаблона',
        status: 'active',
        successRate: 0,
        usageCount: 0
      }],
      defaultModel: templateForm.defaultModel || MODELS[0],
      fallbackModel: templateForm.fallbackModel || MODELS[1],
      maxTokens: templateForm.maxTokens || 4096,
      temperature: templateForm.temperature || 0.7,
      systemPrompt: templateForm.systemPrompt || SYSTEM_PROMPTS[templateForm.type] || '',
      userPromptTemplate: templateForm.userPromptTemplate || `Сгенерируй отчёт "${templateForm.type}" для клиента.\n\nДанные:\n{{birth_date}}, {{birth_time}}, {{birth_place}}\n\nФормат: JSON с секциями.`,
      structure: templateForm.structure || STRUCTURES[templateForm.type] || [],
      variables: templateForm.variables || VARIABLE_SETS[templateForm.type] || [],
      usageCount: 0,
      successRate: 0,
      errorRate: 0,
      avgGenTime: 0,
      lastRun: new Date().toISOString(),
      abTest: null,
      linkedServices: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setData([newTemplate, ...data]);
    setShowCreateModal(false);
    success(`Шаблон ${newId} успешно создан`);
  };
  const handleSaveEdit = () => {
    if (!selectedTemplate) return;
    const updated = {
      ...selectedTemplate,
      ...templateForm
    } as Template;
    setData(data.map(t => t.id === updated.id ? updated : t));
    setSelectedTemplate(updated);
    setEditMode(false);
    success('Изменения сохранены');
  };
  const handleDuplicate = (id: string) => {
    const t = data.find(x => x.id === id);
    if (!t) return;
    const newId = `TPL-${String(data.length + 1).padStart(2, '0')}`;
    const copy: Template = {
      ...t,
      id: newId,
      name: `${t.name} (копия)`,
      status: 'draft',
      usageCount: 0,
      successRate: 0,
      errorRate: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setData([copy, ...data]);
    success(`Шаблон ${id} дублирован как ${newId}`);
  };
  const handleDelete = (id: string) => {
    setData(data.filter(t => t.id !== id));
    success(`Шаблон ${id} удалён`);
    if (selectedTemplate?.id === id) setShowDetail(false);
  };
  const handleTest = () => {
    setIsGenerating(true);
    setTestResult(null);
    setTimeout(() => {
      setIsGenerating(false);
      setTestResult({
        text: `**${selectedTemplate?.name || 'Отчёт'}**\n\nЭто сгенерированный тестовый результат на основе шаблона. В реальной системе здесь будет ответ от AI модели, отформатированный согласно структуре шаблона.\n\n* Солнце в Овне: активность, лидерство.\n* Луна в Тельце: глубокие эмоции, интуиция.\n* Асцендент во Льве: яркая внешность, харизма.\n\nРекомендации: используйте свою энергию для достижения целей, но не забывайте об отдыхе.`,
        tokens: Math.floor(Math.random() * 2000) + 500,
        time: (Math.random() * 15 + 5).toFixed(1),
        cost: (Math.random() * 0.05 + 0.01).toFixed(3)
      });
      success('Тестовая генерация завершена');
    }, 2500);
  };
  const toggleStatus = (id: string) => {
    setData(prev => prev.map(d => {
      if (d.id !== id) return d;
      const cycle: Record<string, Template['status']> = {
        active: 'disabled',
        disabled: 'active',
        draft: 'active',
        testing: 'active'
      };
      const next = cycle[d.status] || 'active';
      success(`Шаблон ${id}: ${STATUS_CONFIG[next]?.label}`);
      return {
        ...d,
        status: next
      };
    }));
  };
  const SortHeader = ({
    field,
    children
  }: {
    field: string;
    children: React.ReactNode;
  }) => <th className="px-3 py-3 font-medium cursor-pointer hover:text-indigo-600 select-none" onClick={() => handleSort(field)} data-id="element-8911">
      <span className="flex items-center gap-1" data-id="element-8912">
        {children}
        {sortField === field && <ChevronDown className={`w-3 h-3 transition-transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} data-id="element-8913" />}
      </span>
    </th>;
  // --- CREATE MODAL ---
  const renderCreateModal = () => {
    if (!showCreateModal) return null;
    return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={() => setShowCreateModal(false)} data-id="element-8914">
        <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={(e: React.MouseEvent) => e.stopPropagation()} data-id="element-8915">
          <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-white shrink-0" data-id="element-8916">
            <div data-id="element-8917">
              <h2 className="text-xl font-bold text-slate-900" data-id="element-8918">Новый шаблон</h2>
              <p className="text-sm text-slate-500" data-id="element-8919">
                Создание нового AI-шаблона
              </p>
            </div>
            <button onClick={() => setShowCreateModal(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100" data-id="element-8920">
              <X className="w-5 h-5" data-id="element-8921" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 bg-slate-50 space-y-6" data-id="element-8922">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8923">
              <div data-id="element-8924">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-8925">
                  Название
                </label>
                <input type="text" className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.name || ''} onChange={e => setTemplateForm({
                ...templateForm,
                name: e.target.value
              })} placeholder="Например: Натальная карта (Lite)" data-id="element-8926" />
              </div>
              <div data-id="element-8927">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-8928">
                  Тип
                </label>
                <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.type || ''} onChange={e => {
                const type = e.target.value;
                setTemplateForm({
                  ...templateForm,
                  type,
                  systemPrompt: SYSTEM_PROMPTS[type] || '',
                  structure: STRUCTURES[type] || [],
                  variables: VARIABLE_SETS[type] || []
                });
              }} data-id="element-8929">
                  <option value="" data-id="element-8930">Выберите тип...</option>
                  {TEMPLATE_TYPES.map(t => <option key={t} value={t} data-id="element-8931">
                      {t}
                    </option>)}
                </select>
              </div>
            </div>
            <div data-id="element-8932">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-8933">
                Описание
              </label>
              <textarea className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.description || ''} onChange={e => setTemplateForm({
              ...templateForm,
              description: e.target.value
            })} rows={2} data-id="element-8934" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8935">
              <div data-id="element-8936">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-8937">
                  Основная модель
                </label>
                <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.defaultModel || ''} onChange={e => setTemplateForm({
                ...templateForm,
                defaultModel: e.target.value
              })} data-id="element-8938">
                  {MODELS.map(m => <option key={m} value={m} data-id="element-8939">
                      {m}
                    </option>)}
                </select>
              </div>
              <div data-id="element-8940">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-8941">
                  Fallback модель
                </label>
                <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.fallbackModel || ''} onChange={e => setTemplateForm({
                ...templateForm,
                fallbackModel: e.target.value
              })} data-id="element-8942">
                  {MODELS.map(m => <option key={m} value={m} data-id="element-8943">
                      {m}
                    </option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8944">
              <div data-id="element-8945">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-8946">
                  Max Tokens
                </label>
                <input type="number" className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.maxTokens || 4096} onChange={e => setTemplateForm({
                ...templateForm,
                maxTokens: parseInt(e.target.value)
              })} data-id="element-8947" />
              </div>
              <div data-id="element-8948">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-8949">
                  Temperature
                </label>
                <input type="number" step="0.1" min="0" max="2" className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.temperature || 0.7} onChange={e => setTemplateForm({
                ...templateForm,
                temperature: parseFloat(e.target.value)
              })} data-id="element-8950" />
              </div>
            </div>
          </div>
          <div className="p-6 border-t border-slate-100 bg-white shrink-0 flex justify-end gap-3" data-id="element-8951">
            <Button variant="outline" onClick={() => setShowCreateModal(false)} data-id="element-8952">
              Отмена
            </Button>
            <Button variant="primary" onClick={handleCreate} data-id="element-8953">
              Создать шаблон
            </Button>
          </div>
        </Card>
      </div>;
  };
  // --- DETAIL MODAL ---
  const renderDetail = () => {
    if (!selectedTemplate) return null;
    const t = selectedTemplate;
    return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={() => setShowDetail(false)} data-id="element-8954">
        <Card className="w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={(e: React.MouseEvent) => e.stopPropagation()} data-id="element-8955">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-white shrink-0" data-id="element-8956">
            <div data-id="element-8957">
              <div className="flex items-center gap-3 mb-1" data-id="element-8958">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-8959">{t.name}</h2>
                <Badge variant={STATUS_CONFIG[t.status]?.variant} data-id="element-8960">
                  {STATUS_CONFIG[t.status]?.label}
                </Badge>
                <Badge variant="outline" size="sm" data-id="element-8961">
                  {t.activeVersion}
                </Badge>
                {t.abTest?.enabled && <Badge variant="pro" size="sm" data-id="element-8962">
                    A/B Test
                  </Badge>}
              </div>
              <p className="text-sm text-slate-500" data-id="element-8963">
                {t.id} • {t.type} • Модель: {t.defaultModel}
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-8964">
              {!editMode && <Button variant="outline" size="sm" icon={<FlaskConical className="w-4 h-4" data-id="element-8966" />} onClick={() => setDetailTab('testing')} data-id="element-8965">
                  Тест
                </Button>}
              {editMode ? <>
                  <Button variant="outline" size="sm" onClick={() => {
                setEditMode(false);
                setTemplateForm({
                  ...t
                });
              }} data-id="element-8967">
                    Отмена
                  </Button>
                  <Button variant="primary" size="sm" icon={<Save className="w-4 h-4" data-id="element-8969" />} onClick={handleSaveEdit} data-id="element-8968">
                    Сохранить
                  </Button>
                </> : <Button variant="outline" size="sm" icon={<PenLine className="w-4 h-4" data-id="element-8971" />} onClick={() => setEditMode(true)} data-id="element-8970">
                  Редактировать
                </Button>}
              <button onClick={() => setShowDetail(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 ml-2" data-id="element-8972">
                <X className="w-5 h-5" data-id="element-8973" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6 border-b border-slate-100 bg-white shrink-0" data-id="element-8974">
            <Tabs activeTab={detailTab} onChange={setDetailTab} tabs={[{
            id: 'main',
            label: 'Основное'
          }, {
            id: 'prompt',
            label: 'Prompt'
          }, {
            id: 'versions',
            label: 'Версии'
          }, {
            id: 'testing',
            label: 'Тестирование'
          }, {
            id: 'analytics',
            label: 'Аналитика'
          }, {
            id: 'links',
            label: 'Связи'
          }]} data-id="element-8975" />
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto bg-slate-50 flex-1" data-id="element-8976">
            {detailTab === 'main' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8977">
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8978">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8979">
                    Информация
                  </h3>
                  {editMode ? <div className="space-y-4" data-id="element-8980">
                      <div data-id="element-8981">
                        <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-8982">
                          Название
                        </label>
                        <input type="text" className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.name || ''} onChange={e => setTemplateForm({
                    ...templateForm,
                    name: e.target.value
                  })} data-id="element-8983" />
                      </div>
                      <div data-id="element-8984">
                        <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-8985">
                          Тип
                        </label>
                        <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.type || ''} onChange={e => setTemplateForm({
                    ...templateForm,
                    type: e.target.value
                  })} data-id="element-8986">
                          {TEMPLATE_TYPES.map(type => <option key={type} value={type} data-id="element-8987">
                              {type}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-8988">
                        <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-8989">
                          Описание
                        </label>
                        <textarea className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.description || ''} onChange={e => setTemplateForm({
                    ...templateForm,
                    description: e.target.value
                  })} rows={3} data-id="element-8990" />
                      </div>
                    </div> : <div className="space-y-3" data-id="element-8991">
                      {[['ID', t.id], ['Название', t.name], ['Тип', t.type], ['Статус', STATUS_CONFIG[t.status]?.label], ['Активная версия', t.activeVersion], ['Описание', t.description]].map(([label, value]) => <div key={label as string} className="flex justify-between py-2 border-b border-slate-100" data-id="element-8992">
                          <span className="text-sm text-slate-500" data-id="element-8993">
                            {label}
                          </span>
                          <span className="text-sm font-medium text-slate-900 text-right max-w-[60%]" data-id="element-8994">
                            {value}
                          </span>
                        </div>)}
                    </div>}
                </Card>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8995">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8996">
                    Модель и параметры
                  </h3>
                  {editMode ? <div className="space-y-4" data-id="element-8997">
                      <div data-id="element-8998">
                        <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-8999">
                          Основная модель
                        </label>
                        <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.defaultModel || ''} onChange={e => setTemplateForm({
                    ...templateForm,
                    defaultModel: e.target.value
                  })} data-id="element-9000">
                          {MODELS.map(m => <option key={m} value={m} data-id="element-9001">
                              {m}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-9002">
                        <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-9003">
                          Fallback модель
                        </label>
                        <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.fallbackModel || ''} onChange={e => setTemplateForm({
                    ...templateForm,
                    fallbackModel: e.target.value
                  })} data-id="element-9004">
                          {MODELS.map(m => <option key={m} value={m} data-id="element-9005">
                              {m}
                            </option>)}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-9006">
                        <div data-id="element-9007">
                          <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-9008">
                            Max Tokens
                          </label>
                          <input type="number" className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.maxTokens || 0} onChange={e => setTemplateForm({
                      ...templateForm,
                      maxTokens: parseInt(e.target.value)
                    })} data-id="element-9009" />
                        </div>
                        <div data-id="element-9010">
                          <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-9011">
                            Temperature
                          </label>
                          <input type="number" step="0.1" min="0" max="2" className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.temperature || 0} onChange={e => setTemplateForm({
                      ...templateForm,
                      temperature: parseFloat(e.target.value)
                    })} data-id="element-9012" />
                        </div>
                      </div>
                    </div> : <div className="space-y-3" data-id="element-9013">
                      {[['Основная модель', t.defaultModel], ['Fallback модель', t.fallbackModel], ['Max Tokens', t.maxTokens.toLocaleString()], ['Temperature', t.temperature.toString()]].map(([label, value]) => <div key={label as string} className="flex justify-between py-2 border-b border-slate-100" data-id="element-9014">
                          <span className="text-sm text-slate-500" data-id="element-9015">
                            {label}
                          </span>
                          <span className="text-sm font-medium text-slate-900" data-id="element-9016">
                            {value}
                          </span>
                        </div>)}
                    </div>}
                  <div className="mt-4" data-id="element-9017">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-9018">
                      Структура отчёта
                    </h4>
                    <div className="flex flex-wrap gap-1.5" data-id="element-9019">
                      {(editMode ? templateForm.structure || [] : t.structure).map((s, i) => <span key={i} className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-[10px] font-medium" data-id="element-9020">
                          {s}
                        </span>)}
                    </div>
                  </div>
                </Card>
                {t.abTest?.enabled && <Card padding="md" className="shadow-sm border-indigo-200 bg-indigo-50/30 md:col-span-2" data-id="element-9021">
                    <div className="flex items-center gap-2 mb-3" data-id="element-9022">
                      <ArrowLeftRight className="w-4 h-4 text-indigo-600" data-id="element-9023" />
                      <h3 className="text-sm font-bold text-indigo-900" data-id="element-9024">
                        A/B Тест активен
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4" data-id="element-9025">
                      <div className="p-3 bg-white rounded-lg border border-indigo-100" data-id="element-9026">
                        <div className="text-xs text-slate-500 mb-1" data-id="element-9027">
                          Вариант A ({t.abTest.split}%)
                        </div>
                        <div className="text-sm font-bold text-slate-900" data-id="element-9028">
                          {t.abTest.variantA}
                        </div>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-indigo-100" data-id="element-9029">
                        <div className="text-xs text-slate-500 mb-1" data-id="element-9030">
                          Вариант B ({100 - t.abTest.split}%)
                        </div>
                        <div className="text-sm font-bold text-slate-900" data-id="element-9031">
                          {t.abTest.variantB}
                        </div>
                      </div>
                    </div>
                  </Card>}
              </div>}

            {detailTab === 'prompt' && <div className="space-y-6" data-id="element-9032">
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9033">
                  <div className="flex items-center justify-between mb-3" data-id="element-9034">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-9035">
                      System Prompt
                    </h4>
                    {!editMode && <Button variant="ghost" size="sm" icon={<Copy className="w-3.5 h-3.5" data-id="element-9037" />} onClick={() => success('Скопировано')} data-id="element-9036">
                        Копировать
                      </Button>}
                  </div>
                  {editMode ? <textarea className="w-full p-4 bg-slate-900 text-emerald-400 rounded-lg font-mono text-xs whitespace-pre-wrap min-h-[200px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.systemPrompt || ''} onChange={e => setTemplateForm({
                ...templateForm,
                systemPrompt: e.target.value
              })} data-id="element-9038" /> : <div className="p-4 bg-slate-900 text-emerald-400 rounded-lg font-mono text-xs whitespace-pre-wrap max-h-[250px] overflow-y-auto leading-relaxed" data-id="element-9039">
                      {t.systemPrompt}
                    </div>}
                </Card>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9040">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-9041">
                    User Prompt Template
                  </h4>
                  {editMode ? <textarea className="w-full p-4 bg-slate-900 text-blue-400 rounded-lg font-mono text-xs whitespace-pre-wrap min-h-[150px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-500" value={templateForm.userPromptTemplate || ''} onChange={e => setTemplateForm({
                ...templateForm,
                userPromptTemplate: e.target.value
              })} data-id="element-9042" /> : <div className="p-4 bg-slate-900 text-blue-400 rounded-lg font-mono text-xs whitespace-pre-wrap max-h-[200px] overflow-y-auto leading-relaxed" data-id="element-9043">
                      {t.userPromptTemplate}
                    </div>}
                </Card>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9044">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-9045">
                    Переменные ({t.variables.length})
                  </h4>
                  <div className="overflow-x-auto" data-id="element-9046">
                    <table className="w-full text-left" data-id="element-9047">
                      <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200" data-id="element-9048">
                        <tr data-id="element-9049">
                          <th className="px-3 py-2" data-id="element-9050">Переменная</th>
                          <th className="px-3 py-2" data-id="element-9051">Тип</th>
                          <th className="px-3 py-2" data-id="element-9052">Обязательная</th>
                          <th className="px-3 py-2" data-id="element-9053">Описание</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-9054">
                        {t.variables.map(v => <tr key={v.name} data-id="element-9055">
                            <td className="px-3 py-2" data-id="element-9056">
                              <code className="text-xs font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded" data-id="element-9057">{`{{${v.name}}}`}</code>
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-600" data-id="element-9058">
                              {v.type}
                            </td>
                            <td className="px-3 py-2" data-id="element-9059">
                              {v.required ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-9060" /> : <span className="text-xs text-slate-400" data-id="element-9061">
                                  Нет
                                </span>}
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-600" data-id="element-9062">
                              {v.description}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>}

            {detailTab === 'versions' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9063">
                <div className="flex items-center justify-between mb-4" data-id="element-9064">
                  <h3 className="text-sm font-bold text-slate-900" data-id="element-9065">
                    История версий ({t.versions.length})
                  </h3>
                  <Button variant="outline" size="sm" icon={<GitBranch className="w-4 h-4" data-id="element-9067" />} onClick={() => success('Создание новой версии')} data-id="element-9066">
                    Новая версия
                  </Button>
                </div>
                <div className="space-y-3" data-id="element-9068">
                  {t.versions.map((v, idx) => <div key={idx} className={`flex items-start gap-4 p-4 rounded-lg border ${v.status === 'active' ? 'border-emerald-200 bg-emerald-50/30' : v.status === 'testing' ? 'border-amber-200 bg-amber-50/30' : 'border-slate-100 bg-white'}`} data-id="element-9069">
                      <div className="shrink-0" data-id="element-9070">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${v.status === 'active' ? 'bg-emerald-100' : v.status === 'testing' ? 'bg-amber-100' : 'bg-slate-100'}`} data-id="element-9071">
                          <GitBranch className={`w-5 h-5 ${v.status === 'active' ? 'text-emerald-600' : v.status === 'testing' ? 'text-amber-600' : 'text-slate-400'}`} data-id="element-9072" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-9073">
                        <div className="flex items-center gap-2 mb-1" data-id="element-9074">
                          <span className="text-sm font-bold text-slate-900" data-id="element-9075">
                            {v.version}
                          </span>
                          <Badge variant={v.status === 'active' ? 'success' : v.status === 'testing' ? 'warning' : 'neutral'} size="sm" data-id="element-9076">
                            {v.status === 'active' ? 'Активна' : v.status === 'testing' ? 'Тест' : 'Архив'}
                          </Badge>
                          <span className="text-xs text-slate-400" data-id="element-9077">
                            • {v.createdBy}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 mb-2" data-id="element-9078">
                          {v.changes}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-slate-500" data-id="element-9079">
                          <span data-id="element-9080">
                            Успешность:{' '}
                            <strong className={v.successRate > 90 ? 'text-emerald-600' : 'text-amber-600'} data-id="element-9081">
                              {v.successRate.toFixed(1)}%
                            </strong>
                          </span>
                          <span data-id="element-9082">
                            Использований: <strong data-id="element-9083">{v.usageCount}</strong>
                          </span>
                          <span data-id="element-9084">
                            {new Date(v.createdAt).toLocaleDateString('ru-RU')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0" data-id="element-9085">
                        {v.status !== 'active' && <Button variant="ghost" size="sm" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-9087" />} onClick={() => success(`Rollback к ${v.version}`)} data-id="element-9086">
                            Откатить
                          </Button>}
                      </div>
                    </div>)}
                </div>
              </Card>}

            {detailTab === 'testing' && <div className="space-y-6" data-id="element-9088">
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9089">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-9090">
                    Sandbox — Тестирование шаблона
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-9091">
                    <div data-id="element-9092">
                      <label className="text-xs font-medium text-slate-500 mb-1 block" data-id="element-9093">
                        Модель
                      </label>
                      <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-9094">
                        {MODELS.map(m => <option key={m} data-id="element-9095">{m}</option>)}
                      </select>
                    </div>
                    <div data-id="element-9096">
                      <label className="text-xs font-medium text-slate-500 mb-1 block" data-id="element-9097">
                        Версия
                      </label>
                      <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-9098">
                        {t.versions.map(v => <option key={v.version} data-id="element-9099">{v.version}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="space-y-3 mb-4" data-id="element-9100">
                    {t.variables.filter(v => v.required).map(v => <div key={v.name} data-id="element-9101">
                          <label className="text-xs font-medium text-slate-600 mb-1 block" data-id="element-9102">
                            {v.description}{' '}
                            <code className="text-indigo-500" data-id="element-9103">{`{{${v.name}}}`}</code>
                          </label>
                          <input type="text" placeholder={`Введите ${v.description.toLowerCase()}`} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-9104" />
                        </div>)}
                  </div>
                  <Button variant="primary" icon={isGenerating ? <RefreshCw className="w-4 h-4 animate-spin" data-id="element-9106" /> : <Play className="w-4 h-4" data-id="element-9107" />} onClick={handleTest} disabled={isGenerating} data-id="element-9105">
                    {isGenerating ? 'Генерация...' : 'Запустить тест'}
                  </Button>
                </Card>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9108">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-9109">
                    Результат теста
                  </h4>
                  {testResult ? <div data-id="element-9110">
                      <div className="flex gap-4 mb-4 text-xs text-slate-500 bg-slate-50 p-2 rounded-lg border border-slate-100" data-id="element-9111">
                        <span data-id="element-9112">
                          Токены:{' '}
                          <strong className="text-slate-900" data-id="element-9113">
                            {testResult.tokens}
                          </strong>
                        </span>
                        <span data-id="element-9114">
                          Время:{' '}
                          <strong className="text-slate-900" data-id="element-9115">
                            {testResult.time} сек
                          </strong>
                        </span>
                        <span data-id="element-9116">
                          Стоимость:{' '}
                          <strong className="text-emerald-600" data-id="element-9117">
                            ${testResult.cost}
                          </strong>
                        </span>
                      </div>
                      <div className="p-4 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-id="element-9118">
                        {testResult.text}
                      </div>
                    </div> : <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 min-h-[120px] flex items-center justify-center" data-id="element-9119">
                      <div className="text-center" data-id="element-9120">
                        <FlaskConical className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-9121" />
                        <p className="text-xs text-slate-400" data-id="element-9122">
                          Запустите тест для просмотра результата
                        </p>
                      </div>
                    </div>}
                </Card>
              </div>}

            {detailTab === 'analytics' && <div className="space-y-6" data-id="element-9123">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-9124">
                  {[{
                label: 'Использований',
                value: t.usageCount.toLocaleString(),
                icon: BarChart3,
                color: 'bg-indigo-100 text-indigo-600'
              }, {
                label: 'Успешность',
                value: `${t.successRate}%`,
                icon: CheckCircle2,
                color: 'bg-emerald-100 text-emerald-600'
              }, {
                label: 'Ошибки',
                value: `${t.errorRate.toFixed(1)}%`,
                icon: AlertTriangle,
                color: 'bg-rose-100 text-rose-600'
              }, {
                label: 'Ср. время',
                value: `${t.avgGenTime} сек`,
                icon: Timer,
                color: 'bg-amber-100 text-amber-600'
              }].map(s => <div key={s.label} className="p-4 bg-white border border-slate-200 rounded-lg" data-id="element-9125">
                      <div className="flex items-center gap-2 mb-2" data-id="element-9126">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${s.color}`} data-id="element-9127">
                          <s.icon className="w-3.5 h-3.5" data-id="element-9128" />
                        </div>
                        <span className="text-xs text-slate-500" data-id="element-9129">
                          {s.label}
                        </span>
                      </div>
                      <div className="text-xl font-bold text-slate-900" data-id="element-9130">
                        {s.value}
                      </div>
                    </div>)}
                </div>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9131">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-9132">
                    Последние генерации
                  </h4>
                  <div className="text-center py-8 text-slate-400" data-id="element-9133">
                    <BarChart3 className="w-10 h-10 mx-auto mb-2 text-slate-300" data-id="element-9134" />
                    <p className="text-xs" data-id="element-9135">
                      Графики использования и ошибок по дням
                    </p>
                    <p className="text-[10px] mt-1" data-id="element-9136">
                      В реальной системе здесь будут recharts графики
                    </p>
                  </div>
                </Card>
              </div>}

            {detailTab === 'links' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9137">
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9138">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-9139">
                    Связанные услуги
                  </h3>
                  <div className="space-y-2" data-id="element-9140">
                    {t.linkedServices.map((s, i) => <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-id="element-9141">
                        <Sparkles className="w-4 h-4 text-indigo-500" data-id="element-9142" />
                        <span className="text-sm text-slate-700" data-id="element-9143">{s}</span>
                      </div>)}
                  </div>
                </Card>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9144">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-9145">
                    Связи с системой
                  </h3>
                  <div className="space-y-3" data-id="element-9146">
                    {[['Генерации', `${t.usageCount} всего`, BarChart3], ['Ошибки', `${Math.floor(t.usageCount * t.errorRate / 100)} ошибок`, AlertTriangle], ['PDF отчёты', `${Math.floor(t.usageCount * 0.85)} создано`, FileText], ['Пользователи', `${Math.floor(t.usageCount * 0.6)} уникальных`, Users]].map(([label, value, Icon]) => <div key={label as string} className="flex items-center justify-between py-2 border-b border-slate-100" data-id="element-9147">
                        <div className="flex items-center gap-2" data-id="element-9148">
                          {createElement(Icon as any, {
                      className: 'w-4 h-4 text-slate-400'
                    })}
                          <span className="text-sm text-slate-600" data-id="element-9149">
                            {label}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-slate-900" data-id="element-9150">
                          {value}
                        </span>
                      </div>)}
                  </div>
                </Card>
              </div>}
          </div>
        </Card>
      </div>;
  };
  // --- MAIN RENDER ---
  return <div className="space-y-6" data-id="element-9151">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-9152">
        {[{
        label: 'Всего шаблонов',
        value: stats.total,
        icon: Layers,
        color: 'bg-slate-100 text-slate-600'
      }, {
        label: 'Активных',
        value: stats.active,
        icon: CheckCircle2,
        color: 'bg-emerald-100 text-emerald-600'
      }, {
        label: 'На тесте',
        value: stats.testing,
        icon: FlaskConical,
        color: 'bg-amber-100 text-amber-600'
      }, {
        label: 'Ср. успешность',
        value: `${stats.avgSuccess}%`,
        icon: TrendingUp,
        color: 'bg-indigo-100 text-indigo-600'
      }, {
        label: 'Проблемных',
        value: stats.problematic,
        icon: ShieldAlert,
        color: stats.problematic > 0 ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 text-slate-400'
      }].map(s => <Card key={s.label} padding="sm" className="shadow-sm border-slate-200" data-id="element-9153">
            <div className="flex items-center gap-3" data-id="element-9154">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${s.color}`} data-id="element-9155">
                <s.icon className="w-4 h-4" data-id="element-9156" />
              </div>
              <div data-id="element-9157">
                <div className="text-xl font-bold text-slate-900" data-id="element-9158">
                  {s.value}
                </div>
                <div className="text-[10px] text-slate-500" data-id="element-9159">{s.label}</div>
              </div>
            </div>
          </Card>)}
      </div>

      {/* Toolbar */}
      <Card padding="none" className="shadow-sm border-slate-200" data-id="element-9160">
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100" data-id="element-9161">
          <div className="flex-1 min-w-[200px] max-w-sm relative" data-id="element-9162">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-9163" />
            <input type="text" placeholder="Поиск по ID, названию, типу..." value={searchQuery} onChange={e => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }} className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-9164" />
          </div>
          <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Filter className="w-4 h-4" data-id="element-9166" />} onClick={() => setShowFilters(!showFilters)} data-id="element-9165">
            Фильтры
          </Button>
          <div className="ml-auto" data-id="element-9167">
            <Button variant="primary" size="sm" icon={<FileCode2 className="w-4 h-4" data-id="element-9169" />} onClick={() => {
            setTemplateForm({
              name: '',
              type: TEMPLATE_TYPES[0],
              description: '',
              defaultModel: MODELS[0],
              fallbackModel: MODELS[1],
              maxTokens: 4096,
              temperature: 0.7,
              systemPrompt: SYSTEM_PROMPTS[TEMPLATE_TYPES[0]],
              userPromptTemplate: `Сгенерируй отчёт "${TEMPLATE_TYPES[0]}" для клиента.\n\nДанные:\n{{birth_date}}, {{birth_time}}, {{birth_place}}\n\nФормат: JSON с секциями.`,
              structure: STRUCTURES[TEMPLATE_TYPES[0]],
              variables: VARIABLE_SETS[TEMPLATE_TYPES[0]]
            });
            setShowCreateModal(true);
          }} data-id="element-9168">
              Создать шаблон
            </Button>
          </div>
        </div>

        {showFilters && <div className="p-4 bg-slate-50 border-b border-slate-100 flex flex-wrap gap-3 items-end" data-id="element-9170">
            {[{
          key: 'status',
          label: 'Статус',
          options: [{
            value: 'all',
            label: 'Все'
          }, ...Object.entries(STATUS_CONFIG).map(([k, v]) => ({
            value: k,
            label: v.label
          }))]
        }, {
          key: 'type',
          label: 'Тип',
          options: [{
            value: 'all',
            label: 'Все'
          }, ...TEMPLATE_TYPES.map(t => ({
            value: t,
            label: t
          }))]
        }, {
          key: 'model',
          label: 'Модель',
          options: [{
            value: 'all',
            label: 'Все'
          }, ...MODELS.map(m => ({
            value: m,
            label: m
          }))]
        }].map(f => <div key={f.key} data-id="element-9171">
                <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1 block" data-id="element-9172">
                  {f.label}
                </label>
                <select value={(filters as any)[f.key]} onChange={e => {
            setFilters(prev => ({
              ...prev,
              [f.key]: e.target.value
            }));
            setCurrentPage(1);
          }} className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-9173">
                  {f.options.map(o => <option key={o.value} value={o.value} data-id="element-9174">
                      {o.label}
                    </option>)}
                </select>
              </div>)}
            <Button variant="ghost" size="sm" onClick={() => {
          setFilters({
            status: 'all',
            type: 'all',
            model: 'all'
          });
          setCurrentPage(1);
        }} data-id="element-9175">
              Сбросить
            </Button>
          </div>}

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-9176">
          <table className="w-full text-left" style={{
          minWidth: '1200px'
        }} data-id="element-9177">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider" data-id="element-9178">
              <tr data-id="element-9179">
                <th className="px-3 py-3 font-medium" data-id="element-9180">ID</th>
                <th className="px-3 py-3 font-medium" data-id="element-9181">Название</th>
                <th className="px-3 py-3 font-medium" data-id="element-9182">Тип</th>
                <th className="px-3 py-3 font-medium" data-id="element-9183">Версия</th>
                <th className="px-3 py-3 font-medium" data-id="element-9184">Статус</th>
                <SortHeader field="usageCount" data-id="element-9185">Использований</SortHeader>
                <SortHeader field="successRate" data-id="element-9186">Успешность</SortHeader>
                <SortHeader field="errorRate" data-id="element-9187">Ошибки</SortHeader>
                <SortHeader field="avgGenTime" data-id="element-9188">Ср. время</SortHeader>
                <th className="px-3 py-3 font-medium" data-id="element-9189">Модель</th>
                <th className="px-3 py-3 font-medium" data-id="element-9190">Посл. запуск</th>
                <th className="px-3 py-3 w-10" data-id="element-9191"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-9192">
              {paginatedData.map(t => <tr key={t.id} className="hover:bg-slate-50 transition-colors group" data-id="element-9193">
                  <td className="px-3 py-3" data-id="element-9194">
                    <button className="text-xs font-bold text-indigo-600 hover:underline" onClick={() => openDetail(t)} data-id="element-9195">
                      {t.id}
                    </button>
                  </td>
                  <td className="px-3 py-3" data-id="element-9196">
                    <button className="text-sm font-medium text-slate-900 hover:text-indigo-600 hover:underline text-left" onClick={() => openDetail(t)} data-id="element-9197">
                      {t.name}
                    </button>
                    {t.abTest?.enabled && <span className="ml-2 text-[9px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded" data-id="element-9198">
                        A/B
                      </span>}
                  </td>
                  <td className="px-3 py-3 text-xs text-slate-600" data-id="element-9199">{t.type}</td>
                  <td className="px-3 py-3" data-id="element-9200">
                    <Badge variant="outline" size="sm" className="text-[10px]" data-id="element-9201">
                      {t.activeVersion}
                    </Badge>
                  </td>
                  <td className="px-3 py-3" data-id="element-9202">
                    <button onClick={() => toggleStatus(t.id)} data-id="element-9203">
                      <Badge variant={STATUS_CONFIG[t.status]?.variant} size="sm" className="cursor-pointer hover:opacity-80" data-id="element-9204">
                        {STATUS_CONFIG[t.status]?.label}
                      </Badge>
                    </button>
                  </td>
                  <td className="px-3 py-3 text-xs font-medium text-slate-900" data-id="element-9205">
                    {t.usageCount.toLocaleString()}
                  </td>
                  <td className="px-3 py-3" data-id="element-9206">
                    <span className={`text-xs font-bold ${t.successRate >= 95 ? 'text-emerald-600' : t.successRate >= 85 ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-9207">
                      {t.successRate}%
                    </span>
                  </td>
                  <td className="px-3 py-3" data-id="element-9208">
                    <span className={`text-xs font-medium ${t.errorRate > 15 ? 'text-rose-600' : t.errorRate > 5 ? 'text-amber-600' : 'text-slate-500'}`} data-id="element-9209">
                      {t.errorRate.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs text-slate-600" data-id="element-9210">
                    {t.avgGenTime} сек
                  </td>
                  <td className="px-3 py-3" data-id="element-9211">
                    <Badge variant="outline" size="sm" className="text-[10px]" data-id="element-9212">
                      {t.defaultModel}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 text-xs text-slate-500" data-id="element-9213">
                    {new Date(t.lastRun).toLocaleDateString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                  </td>
                  <td className="px-3 py-3 relative" data-id="element-9214">
                    <button className="p-1 text-slate-400 hover:text-slate-600 rounded" onClick={e => {
                  e.stopPropagation();
                  setOpenMenuId(openMenuId === t.id ? null : t.id);
                }} data-id="element-9215">
                      <MoreVertical className="w-4 h-4" data-id="element-9216" />
                    </button>
                    {openMenuId === t.id && <div className="absolute right-0 top-9 w-52 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-[9999]" data-id="element-9217">
                        <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    openDetail(t);
                    setOpenMenuId(null);
                  }} data-id="element-9218">
                          <Eye className="w-3.5 h-3.5" data-id="element-9219" /> Открыть шаблон
                        </button>
                        <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    openDetail(t, true);
                    setOpenMenuId(null);
                  }} data-id="element-9220">
                          <PenLine className="w-3.5 h-3.5" data-id="element-9221" /> Редактировать
                        </button>
                        <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    openDetail(t);
                    setDetailTab('testing');
                    setOpenMenuId(null);
                  }} data-id="element-9222">
                          <FlaskConical className="w-3.5 h-3.5" data-id="element-9223" /> Тестировать
                        </button>
                        <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    openDetail(t);
                    setDetailTab('analytics');
                    setOpenMenuId(null);
                  }} data-id="element-9224">
                          <BarChart3 className="w-3.5 h-3.5" data-id="element-9225" /> Аналитика
                        </button>
                        <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    openDetail(t);
                    setDetailTab('versions');
                    setOpenMenuId(null);
                  }} data-id="element-9226">
                          <GitBranch className="w-3.5 h-3.5" data-id="element-9227" /> Версии
                        </button>
                        <div className="h-px bg-slate-100 my-1" data-id="element-9228" />
                        <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    handleDuplicate(t.id);
                    setOpenMenuId(null);
                  }} data-id="element-9229">
                          <Copy className="w-3.5 h-3.5" data-id="element-9230" /> Дублировать
                        </button>
                        <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    toggleStatus(t.id);
                    setOpenMenuId(null);
                  }} data-id="element-9231">
                          <Ban className="w-3.5 h-3.5" data-id="element-9232" />{' '}
                          {t.status === 'active' ? 'Отключить' : 'Включить'}
                        </button>
                        <button className="w-full text-left px-4 py-2 text-xs text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={() => {
                    handleDelete(t.id);
                    setOpenMenuId(null);
                  }} data-id="element-9233">
                          <X className="w-3.5 h-3.5" data-id="element-9234" /> Удалить
                        </button>
                      </div>}
                  </td>
                </tr>)}
              {paginatedData.length === 0 && <tr data-id="element-9235">
                  <td colSpan={12} className="text-center py-12 text-slate-500" data-id="element-9236">
                    <Layers className="w-10 h-10 mx-auto mb-3 text-slate-300" data-id="element-9237" />
                    <p className="text-sm font-medium" data-id="element-9238">Шаблонов не найдено</p>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex items-center justify-between" data-id="element-9239">
            <span className="text-xs text-slate-500" data-id="element-9240">
              Показано {(currentPage - 1) * itemsPerPage + 1}–
              {Math.min(currentPage * itemsPerPage, filteredData.length)} из{' '}
              {filteredData.length}
            </span>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-9241" />
          </div>}
      </Card>

      {showDetail && renderDetail()}
      {showCreateModal && renderCreateModal()}
    </div>;
}